// src/lib/conference/media-link.ts
// Die LiveKit-Verbindung einer Konferenz-Rolle (Host, Gast, Sprecher). Ersetzt seit 2026-09-25 den
// Cloudflare-Realtime-Client (calls.ts: sessions/new, tracks/new, renegotiate, opChain-Serialisierung).
// LiveKit übernimmt Publish/Subscribe, Renegotiation und ICE (TURN kommt über das Signaling aus der
// Server-Config: coturn auf turn-a/turn-b). Diese Klasse kümmert sich nur um drei Dinge:
//   1. Token holen (hinter demselben Gate wie der Raum-WS) und same-origin verbinden,
//   2. nach einem ECHTEN Verbindungsverlust (Box-Failover: Raum lebt nur im RAM der aktiven Box)
//      einen frischen Token holen und neu verbinden — LiveKits eigener Resume greift nur, solange
//      der Server noch derselbe ist (12b §2.2),
//   3. lokale Tracks je Verbindung genau einmal publishen.
import { DisconnectReason, Room, RoomEvent, type RemoteParticipant, type RemoteTrack, type RemoteTrackPublication, type TrackPublishOptions } from 'livekit-client';

/** Same-origin: `wss://<host>` — LiveKit hängt `/rtc/v1` an, Caddy reicht `/rtc*` an 127.0.0.1:7880
 *  (lokal: Vite-Proxy). Keine zweite Adresse, die beim Failover umziehen müsste. */
export function livekitUrl(): string {
  const { protocol, host } = window.location;
  return `${protocol === 'https:' ? 'wss:' : 'ws:'}//${host}`;
}

/** Token beim Dienst holen. Wirft bei jedem Nicht-200 (403 = noch nicht zugelassen / nicht im Raum). */
export async function fetchLivekitToken(url: string, body: unknown): Promise<string> {
  const res = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`livekit token ${res.status}`);
  return ((await res.json()) as { token: string }).token;
}

/** Trennungen, nach denen NICHT neu verbunden wird: eigener Abbau, Kick/Ende (removeParticipant),
 *  Raum gelöscht, oder derselbe Teilnehmer hat sich in einem anderen Tab verbunden. */
const TERMINAL = new Set<DisconnectReason | undefined>([
  DisconnectReason.CLIENT_INITIATED, DisconnectReason.DUPLICATE_IDENTITY,
  DisconnectReason.PARTICIPANT_REMOVED, DisconnectReason.ROOM_DELETED,
]);

export type MediaLinkHooks = {
  /** Frisch verbunden (erstes Mal oder nach Neuverbindung): lokale Tracks publishen, Abos setzen. */
  onConnected?: (room: Room) => void | Promise<void>;
  onSubscribed?: (track: RemoteTrack, pub: RemoteTrackPublication, p: RemoteParticipant) => void;
  onUnsubscribed?: (track: RemoteTrack, pub: RemoteTrackPublication, p: RemoteParticipant) => void;
  /** Nur bei `autoSubscribe: false` nötig: neue Publikation → selbst entscheiden, ob abonniert wird. */
  onPublished?: (pub: RemoteTrackPublication, p: RemoteParticipant) => void;
  onState?: (s: 'live' | 'reconnecting') => void;
  /** Endgültig getrennt (siehe TERMINAL, ohne eigenen Abbau). */
  onTerminal?: (reason: DisconnectReason | undefined) => void;
};

export class MediaLink {
  private room: Room | null = null;
  private closed = true;
  private running = false;
  private waiters: Array<() => void> = [];
  private inflight = new Map<MediaStreamTrack, Promise<unknown>>();

  constructor(
    private fetchToken: () => Promise<string>,
    private autoSubscribe: boolean,
    private hooks: MediaLinkHooks,
  ) {}

  get current(): Room | null { return this.room; }

  /** Verbinden (idempotent). Versucht es weiter, bis `stop()` — Token-403 während des Warteraums
   *  oder einer WS-Grace ist ein normaler Zwischenzustand, kein Fehler. */
  start(): void {
    if (!this.closed) return;
    this.closed = false;
    void this.connectLoop();
  }

  /** Wartet auf eine stehende Verbindung; wirft nach `ms` (UI zeigt dann einen Fehler). */
  waitConnected(ms = 15_000): Promise<Room> {
    if (this.room) return Promise.resolve(this.room);
    return new Promise((resolve, reject) => {
      const t = setTimeout(() => { this.waiters = this.waiters.filter((w) => w !== ok); reject(new Error('Konferenz-Server nicht erreichbar')); }, ms);
      const ok = () => { clearTimeout(t); if (this.room) resolve(this.room); else reject(new Error('Konferenz beendet')); };
      this.waiters.push(ok);
    });
  }

  stop(): void {
    this.closed = true;
    const r = this.room;
    this.room = null;
    this.inflight.clear();
    if (r) void r.disconnect(false);   // lokale MediaStreamTracks gehören den Rollen-Klassen
    const w = this.waiters; this.waiters = [];
    for (const f of w) f();
  }

  /** Lokalen Track in der aktuellen Verbindung publishen — genau einmal je Verbindung. */
  async publish(track: MediaStreamTrack, opts: TrackPublishOptions): Promise<void> {
    const room = this.room;
    if (!room) return;   // wird in onConnected nachgeholt
    for (const pub of room.localParticipant.trackPublications.values()) {
      if (pub.track?.mediaStreamTrack === track) return;
    }
    const laufend = this.inflight.get(track);
    if (laufend) { await laufend; return; }
    const p = room.localParticipant.publishTrack(track, opts);
    this.inflight.set(track, p);
    try { await p; } finally { this.inflight.delete(track); }
  }

  /** Lokalen Track zurückziehen, ohne ihn zu stoppen (das macht der Aufrufer). */
  async unpublish(track: MediaStreamTrack): Promise<void> {
    const room = this.room;
    if (!room) return;
    for (const pub of room.localParticipant.trackPublications.values()) {
      if (pub.track?.mediaStreamTrack === track) { await room.localParticipant.unpublishTrack(pub.track, false); return; }
    }
  }

  private async connectLoop(): Promise<void> {
    if (this.running) return;
    this.running = true;
    let pause = 1000;
    try {
      while (!this.closed) {
        try {
          const token = await this.fetchToken();
          if (this.closed) return;
          const room = this.makeRoom();
          await room.connect(livekitUrl(), token, { autoSubscribe: this.autoSubscribe });
          if (this.closed) { void room.disconnect(false); return; }
          this.room = room;
          this.inflight.clear();
          // Ein Publish-Fehler (z. B. Kamera weg) darf keine zweite Verbindung neben der stehenden auslösen.
          try { await this.hooks.onConnected?.(room); } catch (e) { console.warn('Publish nach dem Verbinden fehlgeschlagen:', e); }
          if (!this.autoSubscribe) {
            for (const p of room.remoteParticipants.values()) for (const pub of p.trackPublications.values()) this.hooks.onPublished?.(pub, p);
          }
          this.hooks.onState?.('live');
          const w = this.waiters; this.waiters = [];
          for (const f of w) f();
          return;
        } catch (e) {
          if (this.closed) return;
          console.warn('LiveKit-Verbindung fehlgeschlagen, neuer Versuch:', e);
          await new Promise((r) => setTimeout(r, pause));
          pause = Math.min(pause * 2, 5000);
        }
      }
    } finally {
      this.running = false;
    }
  }

  private makeRoom(): Room {
    const room = new Room({
      // Kacheln bekommen den rohen MediaStreamTrack (CamTile setzt srcObject selbst, kein attach()).
      // adaptiveStream würde solche Tracks als „nirgends sichtbar“ pausieren → bewusst AUS.
      adaptiveStream: false,
      dynacast: true,
      // 🔴 NIE true: WebAudio im Empfangspfad kostete 760 ms statt 159 ms mouth-to-Nuendo
      // (SP2-E2E-Befund 2, docs/decisions.md). Audio läuft über eigene <audio>-Elemente (audio-mix.ts).
      webAudioMix: false,
      // Die lokalen Tracks gehören den Rollen-Klassen und überleben eine Neuverbindung (Failover).
      stopLocalTrackOnUnpublish: false,
      disconnectOnPageLeave: true,
    });
    room.on(RoomEvent.TrackSubscribed, (t, pub, p) => this.hooks.onSubscribed?.(t, pub, p));
    room.on(RoomEvent.TrackUnsubscribed, (t, pub, p) => this.hooks.onUnsubscribed?.(t, pub, p));
    room.on(RoomEvent.TrackPublished, (pub, p) => { if (!this.autoSubscribe) this.hooks.onPublished?.(pub, p); });
    room.on(RoomEvent.Reconnecting, () => this.hooks.onState?.('reconnecting'));
    room.on(RoomEvent.Reconnected, () => this.hooks.onState?.('live'));
    room.on(RoomEvent.Disconnected, (reason) => {
      if (this.room !== room) return;          // alte Verbindung, schon ersetzt
      this.room = null;
      if (this.closed) return;
      if (TERMINAL.has(reason)) { this.closed = true; this.hooks.onTerminal?.(reason); return; }
      // Knoten weg (Failover/Neustart): frischen Token holen, neu verbinden.
      this.hooks.onState?.('reconnecting');
      void this.connectLoop();
    });
    return room;
  }
}
